<template>
  <form class="position-relative" @submit.prevent="submit">
    <div class="vuedl-layout__closeBtn" @click="close(true)">
      ×
    </div>
    <v-card>
      <v-card-title class="headline grey lighten-2 mb-4 justify-center" primary-title>
        Добавление задания в {{ type.name }}
      </v-card-title>

      <v-card-text>
        <v-select
          label="Группа заданий"
          return-object
          item-text="title"
          item-value="id"
          v-model="form.group"
          :items="taskGroupList"
        />
      </v-card-text>

      <v-card-text>
        <v-select
          label="Тип задания"
          return-object
          item-text="name"
          item-value="id"
          readonly
          v-model="selectedType"
          :items="typesList"
        />
      </v-card-text>

      <v-card-text>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">
                  Названия
                </th>
                <th class="text-left">
                  Заболевание
                </th>
                <th class="text-left">
                  Когда?
                </th>
                <th class="text-center">
                  Критичность
                </th>
                <th class="text-center">
                  <div>Ограничить сбор</div>
                </th>
                <th class="text-center">
                  Начало задания
                </th>
                <th class="text-center">
                  Конец задания
                </th>
                <th class="text-center">
                  Пожизненно?
                </th>
              </tr>
            </thead>
            <tbody>
              <component v-bind:is="'TaskItemRow'" :form="form" :type="type" edit-mode />
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>

      <v-card-text>
        <v-textarea label="Введите комментарий к задаче" dense v-model="form.comment"></v-textarea>
      </v-card-text>

      <v-card-actions>
        <v-btn type="submit" color="primary" text>Создать</v-btn>
        <v-spacer></v-spacer>
        <v-btn type="button" color="primary" text @click="$emit('submit')">Закрыть</v-btn>
      </v-card-actions>
    </v-card>
  </form>
</template>

<script>
import { messageHelper } from "@/services/validation-helper.js";
import DynamicForm from "@/modules/documents/add-document/DynamicForm.vue";
import { formModal } from "@/mixins/modal/form-modal.js";
import Pps from "@/views/pages/tasks/components/Pps.vue";
import Measure from "@/views/pages/tasks/components/Measure.vue";
import DatePicker from "@/components/forms/DatePicker.vue";
import TimePicker from "@/components/forms/TimePicker.vue";
import AutocompleteMedParams from "@/components/AutocompleteMedParams.vue";
import { TasksRepository } from "@/modules/tasks/tasks.repository.js";
import PatientDiseasesList from "@/components/PatientDiseasesList.vue";
import TaskItemRow from "@/views/pages/tasks/components/TaskItemRow.vue";

/**
 * Добавляет произвольное задание в группу(тип) заданий
 */
export default {
  name: "AddTaskItem",
  mixins: [formModal],
  components: {
    PatientDiseasesList,
    AutocompleteMedParams,
    DynamicForm,
    Pps,
    Measure,
    DatePicker,
    TimePicker,
    TaskItemRow,
  },

  props: {
    type: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      /** @type {TaskScheduledEventType[]} typesList */
      typesList: [],
      /** @type {TaskScheduledEventType} selectedType */
      selectedType: { id: this.type.code, name: this.type.name },
      /** @type {TaskDuration[]} durationList */
      durationList: [],
      /** @type {TaskGroup[]} taskGroupList */
      taskGroupList: [],

      /** @type {TaskScheduledEvent} form */
      form: {
        id: null,
        authorId: this.$store.state.auth.id,
        group: null,
        name: null,
        entity: null,
        entityId: null,
        periodicity: null,
        startAt: this.$date().format("YYYY-MM-DD"),
        endAt: null,
        diseases: null,
        critical: false,
        limited: false,
        lifelong: false,
        finished: false,
        order: null,
        factor: null,
        comment: "",
        list: [],
        datetimes: [],
      },
    };
  },

  validations: {
    form: {},
  },

  computed: {
    typeErrors() {
      return messageHelper(this.$v.form.category);
    },
  },

  async created() {
    try {
      const [duration, periodicity, groups, types] = await Promise.all([
        TasksRepository.duration(),
        TasksRepository.periodicity(),
        TasksRepository.groups(this.$store.state.customer.id),
        TasksRepository.types(this.$store.state.customer.id),
      ]);
      this.durationList = duration;
      this.periodicityList = periodicity;
      this.taskGroupList = [{ id: null, title: "Без группы" }, ...groups];
      this.typesList = types;
    } catch (e) {
      console.error(e);
      this.$dialog.notify.error("Ошибка загрузки справочников");
    }
  },

  methods: {
    async submit() {
      // this.$v.form.$touch();
      // if (this.$v.form.$pending || this.$v.form.$error) return;

      if (this.form.entity) {
        this.form.name = this.form.entity.name;
        this.form.entityId = this.form.entity.id;
      }

      this.form.finished = this.$date().isSame(this.$date(this.form.endAt), "day");
      const payload = {
        title: this.form.name,
        diseases: [],
        ui_input_type: "float",
        fields: {
          critical: this.form.critical,
          limited: this.form.limited,
          lifelong: this.form.lifelong,
        },
      };

      if (this.form.diseases) {
        payload.diseases.push(this.form.diseases);
      }

      if (this.form.entity?.measure_id) {
        payload.measure_id = this.form.entity?.measure_id;
      }

      if (this.form.entity?.type) {
        payload.ui_input_type = this.form.entity?.type;
      }

      const datetimesIsEmpty = !this.form.datetimes || this.form.datetimes.length === 0;

      // Формируем объект обновления данных в API
      const postBody = {
        event_schedule_group_id: this.form.group?.id || null,
        entity_type: this.selectedType.id,
        entity_id: this.form.entityId,
        entity_payload: JSON.stringify(payload),
        event_periodicity_id: this.form.periodicity?.id || null,
        event_duration_id: null,
        factor: this.form.factor,
        comment: this.form.comment,
        order: this.form.order,
        start_at: datetimesIsEmpty ? this.form.startAt : null,
        end_at: datetimesIsEmpty ? this.form.endAt : null,
        datetimes: JSON.stringify(this.form.datetimes),
      };

      try {
        const { data } = await TasksRepository.create(this.$store.state.customer.id, postBody);
        this.form.id = data.event_schedule_id;
      } catch (e) {
        this.$dialog.notify.error(`Ошибка добавления задачи "${this.form.name}"`);
        return;
      }

      this.$dialog.notify.success(`Задача "${this.form.name}" добавлена`);
      this.$emit("submit", this.form);
    },
  },
};
</script>

<style scoped></style>
